import type { ArtworkForm, PlacementFormPosition, Side } from "@design-lab/shared/types";

const placementToSideMap: Record<PlacementFormPosition, Side> = {
  front: "front",
  back: "back",
  frontLeftChest: "front"
};

/**
 * Gets the side (front or back) for a given placement.
 *
 * @param placement - The placement position
 * @returns The side that the placement belongs to
 */
export function getSideFromPlacement(placement: PlacementFormPosition): Side {
  return placementToSideMap[placement];
}

/**
 * Resolves a side (front/back) to the concrete placement position, using the
 * selected front sub-position to disambiguate front vs frontLeftChest.
 *
 * This is the inverse of `getSideFromPlacement` and the single source of truth
 * for the synchronous side->placement derivation the Design Lab write paths use
 * to avoid the one-render `currentPlacement` lens lag (MN-7799). It was
 * previously re-implemented inline in `Canvas.handleResize` (MN-7773) and
 * `DesignArtwork` (#57465). Since MN-9145 it also backs
 * `usePlacementNavigation` (entities/artwork), the single write-path authority
 * for placement transitions — there is no third resolver.
 *
 * `selectedFrontPlacement` is typed `PlacementFormPosition` (which includes
 * "back"); a "back" or corrupt value on the front side is normalized to "front"
 * so it can never produce a front write target through the back lens.
 *
 * @param side - The side being written to (front or back)
 * @param selectedFrontPlacement - The currently selected front sub-position
 * @returns The concrete placement position to write to
 */
export function getPlacementFromSide(
  side: Side,
  selectedFrontPlacement: PlacementFormPosition | undefined
): PlacementFormPosition {
  if (side === "back") return "back";
  return selectedFrontPlacement === "frontLeftChest" ? "frontLeftChest" : "front";
}

/**
 * Gets artwork ID for a given side.
 *
 * For front side, it returns whichever artwork ID is available between front or frontLeftChest placements.
 * For back side, it returns the artwork ID of the back placement.
 *
 * @param artworkForm - The artwork form data
 * @param side - The side (front or back)
 * @returns The artwork ID or empty string if not found
 */
export function getArtworkIdBySide(artworkForm: ArtworkForm | undefined, side: Side): string {
  if (!artworkForm) return "";

  if (side === "back") {
    return artworkForm.back?.artworkId || "";
  }

  return artworkForm.front?.artworkId || artworkForm.frontLeftChest?.artworkId || "";
}
